import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Building2, Mail, Phone, MapPin, Sparkles } from "lucide-react";
import { toast } from "sonner";

const PLANS = [
  {
    id: "single_use",
    name: "Uso único",
    sub: "Un solo estudio",
    price: "Pago único",
    bullets: ["Subes 1 dispensación", "Análisis completo", "Exportes PDF/Excel"],
    pro: false,
  },
  {
    id: "subscription_monthly",
    name: "Suscripción mensual",
    sub: "Mes a mes, cancela cuando quieras",
    price: "Mensual",
    bullets: ["Subidas ilimitadas", "Comparativa mes a mes", "Plan de incentivos", "Benchmark provincial"],
    pro: true,
  },
  {
    id: "subscription_annual",
    name: "Suscripción anual",
    sub: "12 meses por adelantado",
    price: "Anual",
    bullets: ["Todo lo de mensual", "Mejor precio anual", "Soporte prioritario"],
    pro: true,
  },
];

export default function OnboardingModal() {
  const { user, refresh } = useAuth();
  const [provinces, setProvinces] = useState([]);
  const [form, setForm] = useState({
    pharmacy_name: "",
    email: "",
    phone: "",
    province: "",
    license_type: "",
    billing_consent: false,
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.get("/provinces").then((r) => setProvinces(r.data || []));
  }, []);

  if (!user || user.onboarded) return null;
  if (user.role === "admin") return null;

  const set = (k) => (e) => setForm({ ...form, [k]: e?.target?.value ?? e });
  const toggleConsent = () => setForm({ ...form, billing_consent: !form.billing_consent });

  const isSub = form.license_type?.startsWith("subscription");

  const submit = async (e) => {
    e.preventDefault();
    if (!form.license_type) {
      toast.error("Elige tu plan");
      return;
    }
    if (isSub && !form.billing_consent) {
      toast.error("Debes aceptar la facturación de la suscripción");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/auth/onboarding", form);
      toast.success("¡Bienvenido a FarmaClass!");
      await refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error en el alta");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[100] bg-slate-950/80 backdrop-blur-sm overflow-y-auto"
      data-testid="onboarding-modal"
    >
      <div className="min-h-screen flex items-start justify-center p-4 sm:p-8 py-12">
        <div className="bg-white w-full max-w-3xl rounded-lg shadow-2xl overflow-hidden">
          <div className="bg-gradient-to-br from-slate-950 to-slate-800 text-white p-8">
            <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-300 mb-2">
              Licencia {user.license_code}
            </div>
            <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tighter">Bienvenido a FarmaClass</h1>
            <p className="text-slate-300 mt-2 text-sm">Completa los datos de tu farmacia y elige tu plan para continuar.</p>
          </div>

          <form onSubmit={submit} className="p-8 space-y-7">
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-500 mb-3">Datos de la farmacia</div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Field icon={Building2} label="Nombre de la farmacia" required>
                  <input
                    data-testid="onb-pharmacy"
                    type="text"
                    value={form.pharmacy_name}
                    onChange={set("pharmacy_name")}
                    className="w-full bg-transparent outline-none"
                    required
                  />
                </Field>
                <Field icon={Mail} label="Email" required>
                  <input
                    data-testid="onb-email"
                    type="email"
                    value={form.email}
                    onChange={set("email")}
                    className="w-full bg-transparent outline-none"
                    required
                  />
                </Field>
                <Field icon={Phone} label="Teléfono" required>
                  <input
                    data-testid="onb-phone"
                    type="tel"
                    value={form.phone}
                    onChange={set("phone")}
                    className="w-full bg-transparent outline-none"
                    required
                  />
                </Field>
                <Field icon={MapPin} label="Provincia" required>
                  <select
                    data-testid="onb-province"
                    value={form.province}
                    onChange={set("province")}
                    className="w-full bg-transparent outline-none"
                    required
                  >
                    <option value="">Selecciona…</option>
                    {provinces.map((p) => (
                      <option key={p.province} value={p.province}>{p.province}</option>
                    ))}
                  </select>
                </Field>
              </div>
            </div>

            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-500 mb-3">Elige tu plan</div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                {PLANS.map((p) => {
                  const selected = form.license_type === p.id;
                  return (
                    <button
                      key={p.id}
                      type="button"
                      data-testid={`onb-plan-${p.id}`}
                      onClick={() => setForm({ ...form, license_type: p.id })}
                      className={`text-left rounded-lg border-2 p-4 transition-all ${
                        selected
                          ? "border-slate-950 bg-slate-50"
                          : "border-slate-200 hover:border-slate-400 bg-white"
                      }`}
                    >
                      <div className="flex items-center justify-between mb-1">
                        <span className="font-display font-bold text-slate-900">{p.name}</span>
                        {p.pro && (
                          <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 bg-slate-950 text-white rounded font-bold inline-flex items-center gap-1">
                            <Sparkles className="h-2.5 w-2.5" /> PRO
                          </span>
                        )}
                      </div>
                      <div className="text-[11px] text-slate-500 mb-3">{p.sub}</div>
                      <ul className="text-xs text-slate-700 space-y-1">
                        {p.bullets.map((b) => (
                          <li key={b}>· {b}</li>
                        ))}
                      </ul>
                    </button>
                  );
                })}
              </div>
            </div>

            {isSub && (
              <label
                data-testid="onb-billing-consent"
                className={`flex items-start gap-3 rounded-lg border-2 p-4 cursor-pointer transition-colors ${
                  form.billing_consent ? "border-emerald-500 bg-emerald-50" : "border-amber-200 bg-amber-50"
                }`}
              >
                <input
                  type="checkbox"
                  checked={form.billing_consent}
                  onChange={toggleConsent}
                  className="mt-1 h-4 w-4"
                />
                <div className="text-sm text-slate-700">
                  <strong>Acepto la facturación recurrente</strong> de la suscripción {form.license_type === "subscription_annual" ? "anual" : "mensual"}. FarmaClass me contactará con los datos de facturación.
                </div>
              </label>
            )}

            <div className="flex justify-end pt-4 border-t border-slate-100">
              <button
                type="submit"
                disabled={submitting}
                data-testid="onb-submit"
                className="bg-slate-950 text-white px-6 py-3 rounded-md text-sm font-medium hover:bg-slate-800 disabled:opacity-50"
              >
                {submitting ? "Activando…" : "Activar mi cuenta"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

function Field({ icon: Icon, label, required, children }) {
  return (
    <label className="block">
      <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1">
        {label}{required && <span className="text-rose-500"> *</span>}
      </div>
      <div className="flex items-center gap-2 px-3 py-2.5 bg-white border border-slate-200 rounded-md focus-within:border-slate-900 focus-within:ring-1 focus-within:ring-slate-900">
        <Icon className="h-4 w-4 text-slate-400" strokeWidth={1.5} />
        {children}
      </div>
    </label>
  );
}
